import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Box, Button, MenuItem, TextField, Typography } from "@mui/material";
import { BaseModal } from "../../components/Modal/BaseModal";
import { EditableGrid } from "../../components/DataTable/EditableGrid";
import { Add } from "@mui/icons-material";
import moment from "moment";
import { useAppDispatch } from "../../store/hooks";
import { fetchProductPriceUnits } from "../../slices/productcostapplication.slice";
import { validatePricePeriodsGridData } from "../Modals/action-bill-modal/util";
import { v4 as uuidv4 } from "uuid";
import { EditDescriptor } from "@progress/kendo-react-data-tools";
import { ActionCell } from "../../components/DataTable/customcell/ActionCell";
import { savePlantingCostData } from "../../slices/productcostseeds.slice";
import { useAppSelector } from "../../store/hooks";
import { RootState } from "../../store";
import { useAddCost } from "../../hooks/useAddCost";
import { PlantingActivityT } from "../../types/addCost";
import type { KeyboardEvent } from "react";

interface Props {
  open: boolean;
  handleClose: () => void;
  editItem: any;
  isCreate?: boolean;
}
const newRow = {
  notes: "",
  firstPlanted: new Date(),
  lastPlanted: new Date(),
};

export const EditPlantingPriceModal = ({
  handleClose,
  open,
  editItem,
  isCreate,
}: Props) => {
  const [tableData, setTableData] = useState<any[]>([]);
  const [errorFields, setErrorFields] = useState<string[]>([]);
  const [edit, setEdit] = useState<EditDescriptor>({});
  const [rateTypeValue, setRateTypeValue] = useState("");
  const [workName, setWorkName] = useState("");
  const [machineName, setMachineName] = useState("");
  const workNameInputRef = useRef<HTMLInputElement | null>(null);
  const machineNameInputRef = useRef<HTMLInputElement | null>(null);
  const lastFocusedFieldRef = useRef<"workName" | "machineName" | null>(null);

  const dispatch = useAppDispatch();
  const { handleAddPlantingActivity } = useAddCost();
  const { rateTypesArray } = useAppSelector((state: RootState) => state.seedsdropdown);

  useEffect(() => {
    dispatch(fetchProductPriceUnits());
  }, [dispatch]);

  useEffect(() => {
    if (!open) {
      return;
    }

    if (editItem) {
      const { periods } = editItem;
      setRateTypeValue(editItem.rateType || "");
      setWorkName(editItem.workName || "");
      setMachineName(editItem.machineName || "");
      setTableData(
        periods.map((item: any) => {
          return {
            ...item,
            id: uuidv4(),
            firstPlanted: moment(item.firstPlanted, "MM/DD/YYYY").toDate(),
            lastPlanted: moment(item.lastPlanted, "MM/DD/YYYY").toDate(),
          };
        })
      );
    } else {
      setRateTypeValue("");
      setWorkName("");
      setMachineName("");
      setTableData([]);
    }
  }, [open, isCreate, editItem?.id]);

  const canSave = !!(
    tableData.length &&
    !errorFields.length &&
    rateTypeValue &&
    workName.trim() &&
    machineName.trim()
  );

  useLayoutEffect(() => {
    if (!open || !canSave) {
      return;
    }

    if (document.activeElement === document.body) {
      if (lastFocusedFieldRef.current === "workName") {
        workNameInputRef.current?.focus();
      }
      if (lastFocusedFieldRef.current === "machineName") {
        machineNameInputRef.current?.focus();
      }
    }
  }, [open, canSave]);

  const addNewPeriod = () => {
    setTableData((prev) => [...prev, { ...newRow, id: uuidv4() }]);
  };

  const handleHeaderInputKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      event.preventDefault();
      event.stopPropagation();
    }
  };

  const onItemChange = useCallback((e: any) => {
    const { field, value, dataItem } = e;
    setTableData((prevData) =>
      prevData.map((item: any) => {
        if (item.id === dataItem.id) {
          const updatedRow = {
            ...item,
          };
          updatedRow[field] = value;
          return updatedRow;
        }
        return item;
      })
    );
  }, []);

  useEffect(() => {
    setErrorFields(
      validatePricePeriodsGridData(tableData, {
        costPerAcre: "Cost/Acre",
        firstPlanted: "First Planted Date",
        lastPlanted: "Last Planted Date",
      })
    );
    setEdit(
      tableData.reduce((acc, item) => {
        acc[item.id] = true;
        return acc;
      }, {})
    );
  }, [tableData]);

  const handleSave = async () => {
    const formattedPeriods = tableData.map((item: any) => {
      return {
        costPerAcre: Number(item.costPerAcre) || 0,
        firstPlanted: moment(item.firstPlanted).format("YYYY-MM-DD"),
        lastPlanted: moment(item.lastPlanted).format("YYYY-MM-DD"),
        notes: item.notes,
      };
    });

    if (isCreate) {
      const createPayload: PlantingActivityT = {
        workName: workName.trim(),
        machineName: machineName.trim(),
        activity: workName.trim(),
        rateTypeMappings: [
          {
            rateType: rateTypeValue,
            periods: formattedPeriods,
          },
        ],
      };

      const isSuccess = await handleAddPlantingActivity(createPayload);
      if (isSuccess) {
        handleClose();
      }
      return;
    }

    const requestData = {
      periods: formattedPeriods,
    };

    await dispatch(
      savePlantingCostData({
        ...requestData,
        id: editItem.id,
        machineName: editItem.machineName,
        rateType: editItem.rateType,
      })
    );

    handleClose();
  };

  const deletePricePeriod = useCallback((dataItem: any) => {
    setTableData((prevData) => prevData.filter((item: any) => item.id !== dataItem.id));
  }, []);

  const gridColumns: any[] = useMemo(
    () => [
      {
        field: "costPerAcre",
        title: "Cost/Acre",
        editor: "numeric",
        width: 150,
      },
      {
        field: "firstPlanted",
        title: "First Planted Date",
        editor: "date",
        format: "{0:d}",
        width: 150,
      },
      {
        field: "lastPlanted",
        title: "Last Planted Date",
        editor: "date",
        format: "{0:d}",
        width: 150,
      },
      {
        field: "notes",
        title: "Notes",
        width: 180,
      },
      {
        title: "Edit Actions",
        width: 100,
        cells: {
          data: (props: any) => (
            <ActionCell {...props} onDelete={deletePricePeriod} />
          ),
        },
      },
    ],
    [deletePricePeriod]
  );

  return (
    <BaseModal open={open} handleClose={handleClose}>
      <Box display={"flex"} flexDirection={"column"} gap={5}>
        <Typography variant="h6">{isCreate ? "Create Planting Price" : "Edit Planting Price"}</Typography>
        <Box display={"flex"} flexWrap={"wrap"} gap={2}>
          <TextField
            select
            label="Rate Type"
            variant="outlined"
            value={rateTypeValue}
            sx={{ minWidth: 250 }}
            onChange={(event) => setRateTypeValue(event.target.value)}
          >
            {rateTypesArray.map((rateObj) => (
              <MenuItem key={rateObj.id} value={rateObj.name}>
                {rateObj.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Work Name"
            variant="outlined"
            value={workName}
            sx={{ minWidth: 250 }}
            inputRef={workNameInputRef}
            onFocus={() => {
              lastFocusedFieldRef.current = "workName";
            }}
            onKeyDown={handleHeaderInputKeyDown}
            onChange={(event) => {
              setWorkName(event.target.value);
              const el = workNameInputRef.current;
              if (el) {
                const pos = el.selectionStart;
                requestAnimationFrame(() => {
                  if (document.activeElement !== el) {
                    el.focus();
                    try { el.setSelectionRange(pos, pos); } catch (_) {}
                  }
                });
              }
            }}
            InputProps={{
              readOnly: !isCreate,
            }}
          />
          <TextField
            label="Machine Name"
            variant="outlined"
            style={{ width: "250px" }}
            value={machineName}
            inputRef={machineNameInputRef}
            onFocus={() => {
              lastFocusedFieldRef.current = "machineName";
            }}
            onKeyDown={handleHeaderInputKeyDown}
            onChange={(event) => {
              setMachineName(event.target.value);
              const el = machineNameInputRef.current;
              if (el) {
                const pos = el.selectionStart;
                requestAnimationFrame(() => {
                  if (document.activeElement !== el) {
                    el.focus();
                    try { el.setSelectionRange(pos, pos); } catch (_) {}
                  }
                });
              }
            }}
            InputProps={{
              readOnly: !isCreate,
            }}
          />
        </Box>
        <Box>
          <Box
            display={"flex"}
            justifyContent={"space-between"}
            mb={1}
            alignItems={"center"}
          >
            <Typography>Price Periods</Typography>
            <Button
              type="button"
              variant="contained"
              color="primary"
              startIcon={<Add />}
              onMouseDown={(event) => event.preventDefault()}
              onClick={addNewPeriod}
            >
              Add Period
            </Button>
          </Box>
          <EditableGrid
            dataItemKey="id"
            onItemChange={onItemChange}
            edit={edit}
            columns={gridColumns}
            data={tableData}
          />
        </Box>
        <Box display={"flex"} justifyContent={"flex-end"} gap={2}>
          <Button
            variant="contained"
            color="primary"
            disabled={!canSave}
            onClick={handleSave}
          >
            Save
          </Button>
          <Button variant="outlined" color="error" onClick={handleClose}>
            Cancel
          </Button>
        </Box>
      </Box>
    </BaseModal>
  );
};
